import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator, FlatList, Pressable,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import MonitorIcon from '../MonitorIcon';
import Button from '../Button';
import DownArrow24 from '../24pxIcons/DownArrow24';
import AlertModal from '../AlertModal';
import leaveUserGroup from '../../requests/Users/LeaveUserGroup';
import isMobile from '../IsMobile';
import LeaveGroupIcon from '../24pxIcons/LeaveGroupIcon';
import ScreenEmptyState from '../ScreenEmptyState';
import NoUsersImage from '../UserManagement/NoUsersImage';
import RightArrow24 from '../24pxIcons/RightArrow24';
import { EXIT_USER_GROUP_FAILURE, EXIT_USER_GROUP_SUCCESS } from '../../constants/AlertMessages';
import PressableStyled, { PressableState } from '../PressableStyled';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import { UserPermission } from '../../constants/Types';

export type MyUserGroup = {
  Name: string,
  Description: string,
  UserGroupId: string,
}

interface MyGroupsListProps {
  email: string,
  myUserGroups: MyUserGroup[],
  setMyUserGroups: Dispatch<SetStateAction<MyUserGroup[]>>,
  client: string,
}

const MyGroupsList = ({
  myUserGroups, setMyUserGroups, email, client,
}: MyGroupsListProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const userGroups = myUserGroups?.reduce((prev, current: MyUserGroup) => {
    if (current?.UserGroupId) {
      prev[current.UserGroupId] = current;
    }
    return prev;
  }, {});
  const navigation = useNavigation();
  const allowedToLeaveGroup: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_UPDATE);

  useEffect(() => {
    if (myUserGroups) setLoading(false);
  }, [myUserGroups]);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const leaveError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? EXIT_USER_GROUP_FAILURE,
  );

  const navigateToUserManagement: () => void = () => {
    navigation.navigate(AppScreen.USER_MANAGEMENT as any);
  };

  return <>
    <FlatList
      testID='user-group-list'
      style={styles.container}
      contentContainerStyle={styles.content}
      data={Object.keys(userGroups ?? {})}
      renderItem={({ item }) => {
        const leave = () => {
          showAlert(
            `Leave ${userGroups[item].Name}?`,
            'Are you sure you wish to leave this user group?',
            true,
            () => {
              const groupName: string = userGroups[item].Name;
              const groupId: string = item;
              setLoading(true);
              leaveUserGroup(`${client}-${email}`, groupId, groupName, buildingId)
                .then((res) => {
                  if (res && !res.isError) {
                    setLoading(false);
                    setMyUserGroups(myUserGroups
                      .filter((group: MyUserGroup) => group
                        ?.UserGroupId !== groupId));
                    showAlert(`Left ${groupName}`, EXIT_USER_GROUP_SUCCESS, false);
                  } else leaveError(res?.errorText);
                }).catch((error) => leaveError(error?.message));
            },
            'Leave',
          );
        };

        return item ? <Pressable
          style={({ hovered, pressed }: PressableState) => [
            Style.row, styles.item,
            hovered && Style.listItemHovered, pressed && Style.listItemPressed,
          ]}
          onPress={navigateToUserManagement}
        >
          <View style={styles.itemContent}>
            <View style={styles.iconContainer}>
              <MonitorIcon color='#FFF' />
            </View>
            <View style={styles.textContainer}>
              <Text style={styles.groupName}>
                {userGroups[item].Name}
              </Text>
              <Text style={styles.groupDescription}>
                {userGroups[item].Description}
              </Text>
            </View>
          </View>
          {allowedToLeaveGroup && (isMobile ? <PressableStyled
            onPress={leave}
            style={styles.leaveGroupIcon}
          >
            <LeaveGroupIcon />
          </PressableStyled>
            : <Button
              title='Leave'
              onPress={leave}
              style={styles.leaveButton}
              children={<DownArrow24
                style={styles.rightArrowIcon}
                color={Colors.light.hereworksBlue500}
              />}
              testID='leave-group-btn'
            />)}
        </Pressable> : null;
      }}
      keyExtractor={(item: string) => `my_group_item_${item}`}
      ListEmptyComponent={!loading && <ScreenEmptyState
        image={<NoUsersImage />}
        title={userGroups ? 'User not in any groups' : 'Unable to load list of user groups'}
        subtitle={userGroups
          ? `Go to User management to join a user group\n${isMobile ? '(User Management only available on the desktop app)' : ''}`
          : 'Refresh the page to check again'}
        buttonTitle={(!isMobile && userGroups) ? 'Go to User management' : null}
        actionAvailable={!isMobile}
        buttonAction={(!isMobile && userGroups) ? () => {
          navigation.navigate(AppScreen.USER_MANAGEMENT as any);
        } : null}
        buttonStyle={styles.emptyStateButton}
        buttonIcon={<RightArrow24
          color={'#FFF'}
          style={styles.emptyStateButtonIcon}
        />}
      />}
    />
    <ActivityIndicator
      animating={loading}
      size='large'
      style={Style.activityIndicator}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 20,
    marginHorizontal: isMobile ? 24 : 0,
    borderRadius: 24,
    backgroundColor: '#FFF',
    maxWidth: 820,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 18,
    paddingVertical: isMobile ? 20 : 0,
  },
  item: {
    paddingVertical: isMobile ? 16 : 20,
    paddingHorizontal: isMobile ? 16 : 24,
    marginVertical: isMobile ? 8 : 4,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: isMobile ? Colors.light.hereworksWhite300
      : Colors.light.hereworksBlue100,
  },
  itemContent: {
    flexDirection: isMobile ? 'column' : 'row',
    flex: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: Colors.light.hereworksDarkBlue,
    justifyContent: 'center',
    alignItems: 'center',
  },
  textContainer: {
    marginHorizontal: isMobile ? 0 : 12,
    marginTop: isMobile ? 12 : 0,
    flex: 1,
  },
  groupName: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  groupDescription: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  leaveButton: {
    paddingStart: 12,
    paddingEnd: 4,
    paddingVertical: 5,
  },
  rightArrowIcon: {
    marginStart: 8,
    transform: [{ rotate: '270deg' }],
  },
  leaveGroupIcon: {
    position: 'absolute',
    top: 24,
    end: 24,
  },
  emptyStateButton: {
    flexDirection: 'row',
  },
  emptyStateButtonIcon: {
    marginStart: 8,
  },
});

export default MyGroupsList;
